import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSyncWatch } from "../context/SyncContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, Check, X as XIcon, Pencil, ArrowLeftCircle, History } from "lucide-react";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";
import AvailabilityMatrix from "./concediu/AvailabilityMatrix";

const STATUS_LABELS = { pending: "În așteptare", approved: "Aprobat", rejected: "Respins", returned: "Revenit" };
const STATUS_BADGES = {
  pending: "bg-amber-100 text-amber-700 border-amber-300",
  approved: "bg-green-100 text-green-700 border-green-300",
  rejected: "bg-red-100 text-red-700 border-red-300",
  returned: "bg-zinc-100 text-zinc-700 border-zinc-300",
};
const STATUS_ROW = {
  pending: "border-2 border-red-500 bg-red-50/60 animate-pulse-red-soft",
  approved: "border-l-4 border-l-green-500",
  rejected: "border-l-4 border-l-red-500",
  returned: "border-l-4 border-l-zinc-400",
};

const todayStr = () => new Date().toISOString().slice(0, 10);
// A vacation has "ended" when today is after the end_date AND status is approved
const hasEnded = (v) => {
  if (!v || v.status !== "approved") return false;
  const end = v.end_date || "";
  if (!end) return false;
  return todayStr() > end;
};

const blankForm = { person_id: "", person_name: "", start_date: "", end_date: "", note: "" };

export default function PlanificatorConcediu() {
  const confirm = useConfirm();
  const { user, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [persons, setPersons] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // vacation row being edited
  const [form, setForm] = useState(blankForm);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const r = await api.get("/vacations", { params: { status: "returned" } });
      setHistoryRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea istoricului");
      setHistoryRows([]);
    } finally { setHistoryLoading(false); }
  };

  const load = async () => {
    try {
      const [v, p] = await Promise.all([
        api.get("/vacations"),
        api.get("/persons").catch(() => ({ data: [] })),
      ]);
      setRows(v.data || []);
      setPersons((p.data || []).filter((x) => x.active !== false));
    } catch { toast.error("Eroare la încărcare"); }
  };
  useEffect(() => { load(); }, []);
  useSyncWatch(["vacations", "persons"], () => { load(); });

  const openCreate = () => {
    setEditing(null);
    if (isAdmin) {
      setForm(blankForm);
    } else {
      // Employee: lock to own person
      const me = persons.find((p) => p.id === user?.person_id);
      setForm({
        ...blankForm,
        person_id: user?.person_id || "",
        person_name: me ? `${me.nume || ""} ${me.prenume || ""}`.trim() : "",
      });
    }
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      person_id: row.person_id || "",
      person_name: row.person_name || "",
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      note: row.note || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (!form.start_date || !form.end_date) {
        toast.error("Completează ambele date");
        return;
      }
      if (form.end_date < form.start_date) {
        toast.error("Data de sfârșit nu poate fi înaintea celei de început");
        return;
      }
      const payload = {
        person_id: form.person_id || null,
        person_name: form.person_name || null,
        start_date: form.start_date,
        end_date: form.end_date,
        note: form.note || null,
      };
      if (editing) {
        const res = await api.put(`/vacations/${editing.id}`, payload);
        const removed = res?.data?.schedule_entries_affected || 0;
        toast.success(
          (isAdmin ? "Concediu actualizat" : "Cerere actualizată — în așteptare") +
          (removed > 0 ? ` · ${removed} alocări scoase din planificator` : "")
        );
      } else {
        const res = await api.post("/vacations", payload);
        const removed = res?.data?.schedule_entries_affected || 0;
        toast.success(
          (isAdmin ? "Concediu adăugat" : "Cerere trimisă — în așteptare aprobare") +
          (removed > 0 ? ` · ${removed} alocări scoase din planificator` : "")
        );
      }
      setOpen(false);
      setEditing(null);
      setForm(blankForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    }
  };

  const remove = async (row) => {
    if (!(await confirm({ body: `Ștergi concediul lui ${row.person_name || "—"} (${fmtRoDate(row.start_date)} → ${fmtRoDate(row.end_date)})?`, danger: true }))) return;
    try {
      await api.delete(`/vacations/${row.id}`);
      toast.success("Concediu șters");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const approve = async (row) => {
    try {
      const res = await api.patch(`/vacations/${row.id}/approve`);
      const removed = res?.data?.schedule_entries_affected || 0;
      toast.success("Concediu aprobat" + (removed > 0 ? ` · ${removed} alocări scoase din planificator` : ""));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const confirmReturned = async (row) => {
    if (!(await confirm({ body: `Confirmi revenirea din concediu pentru ${row.person_name || "—"}? Persoana va fi disponibilă pentru alocări și mutată în istoric.` }))) return;
    try {
      await api.patch(`/vacations/${row.id}/confirm-returned`);
      toast.success(`${row.person_name || "Persoana"} a revenit din concediu`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const submitReject = async () => {
    try {
      await api.patch(`/vacations/${rejectingId}/reject`, { reject_reason: rejectReason });
      toast.success("Concediu respins");
      setRejectingId(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    for (const r of rows) c[r.status || "pending"] = (c[r.status || "pending"] || 0) + 1;
    return c;
  }, [rows]);

  const canEditRow = (r) =>
    isAdmin || (r.person_id === user?.person_id && (!r.status || r.status === "pending"));
  const canDeleteRow = (r) =>
    isAdmin || (r.person_id === user?.person_id && (!r.status || r.status === "pending"));

  return (
    <div data-testid="page-concediu">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Planificator Concediu</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isAdmin
              ? "Aprobă cererile angajaților și planifică perioadele"
              : "Adaugă, modifică sau șterge cererile tale de concediu (până la aprobare)"}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-2 text-xs">
            <span className={`px-2 py-1 rounded border ${STATUS_BADGES.pending}`} data-testid="count-pending">{counts.pending} în așteptare</span>
            <span className={`px-2 py-1 rounded border ${STATUS_BADGES.approved}`} data-testid="count-approved">{counts.approved} aprobate</span>
            <span className={`px-2 py-1 rounded border ${STATUS_BADGES.rejected}`} data-testid="count-rejected">{counts.rejected} respinse</span>
          </div>
          {isAdmin && (
            <Button
              data-testid="history-vacations-btn"
              variant="outline"
              onClick={openHistory}
              className="font-semibold"
              title="Persoane care au revenit din concediu"
            >
              <History size={16}/> Istoric Persoane Concediu
            </Button>
          )}
          {(isAdmin || user?.person_id) && (
            <Button
              data-testid="add-vacation-btn"
              className="bg-white border border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700 font-semibold"
              onClick={openCreate}
            >
              <Plus size={16}/> {isAdmin ? "Concediu nou" : "Cere concediu"}
            </Button>
          )}
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="p-3">Persoană</th>
              <th className="p-3">Început</th>
              <th className="p-3">Sfârșit</th>
              <th className="p-3 text-center">Zile</th>
              <th className="p-3">Status</th>
              <th className="p-3">Notă</th>
              <th className="p-3 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const status = r.status || "pending";
              const ended = hasEnded(r);
              const rowClass = ended
                ? "border-2 border-red-500 bg-red-50/50"
                : (STATUS_ROW[status] || "");
              return (
                <tr key={r.id} className={`border-t border-[var(--border)] row-hover ${rowClass}`} data-testid={`vacation-row-${r.id}`}>
                  <td className="p-3 font-medium">
                    <CalendarDays size={14} className={`inline-block mr-1 ${ended ? "text-red-500" : status === "pending" ? "text-red-500" : "text-zinc-400"}`} />
                    {r.person_name || "—"}
                    {ended && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded animate-pulse">
                        Concediu încheiat
                      </span>
                    )}
                    {!ended && status === "pending" && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded animate-pulse" data-testid={`badge-pending-${r.id}`}>
                        ⚠ De aprobat
                      </span>
                    )}
                  </td>
                  <td className="p-3 tabular-nums">{fmtRoDate(r.start_date)}</td>
                  <td className="p-3 tabular-nums">{fmtRoDate(r.end_date)}</td>
                  <td className="p-3 text-center tabular-nums" data-testid={`vacation-days-${r.id}`}>
                    {(() => {
                      try {
                        const sd = new Date(r.start_date);
                        const ed = new Date(r.end_date);
                        if (isNaN(sd) || isNaN(ed)) return "—";
                        const days = Math.floor((ed - sd) / 86400000) + 1;
                        return days > 0 ? <span className="font-semibold">{days}</span> : "—";
                      } catch { return "—"; }
                    })()}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded border ${STATUS_BADGES[status]}`} data-testid={`vacation-status-${r.id}`}>
                      {STATUS_LABELS[status]}
                    </span>
                    {status === "rejected" && r.reject_reason && (
                      <div className="text-[11px] text-red-600 mt-1 italic">{r.reject_reason}</div>
                    )}
                  </td>
                  <td className="p-3 text-zinc-600">{r.note || "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {isAdmin && ended && (
                      <Button
                        size="sm"
                        onClick={() => confirmReturned(r)}
                        data-testid={`confirm-returned-${r.id}`}
                        className="bg-red-600 hover:bg-red-700 text-white font-semibold mr-2 h-7 text-xs"
                        title="Confirmă revenirea din concediu"
                      >
                        <ArrowLeftCircle size={14}/> Confirmă revenirea
                      </Button>
                    )}
                    {isAdmin && status === "pending" && (
                      <>
                        <button
                          onClick={() => approve(r)}
                          className="text-green-700 p-1 hover:bg-green-50 rounded mr-1"
                          title="Aprobă"
                          data-testid={`approve-vacation-${r.id}`}
                        >
                          <Check size={16}/>
                        </button>
                        <button
                          onClick={() => { setRejectingId(r.id); setRejectReason(""); }}
                          className="text-red-600 p-1 hover:bg-red-50 rounded mr-1"
                          title="Respinge"
                          data-testid={`reject-vacation-${r.id}`}
                        >
                          <XIcon size={16}/>
                        </button>
                      </>
                    )}
                    {canEditRow(r) && (
                      <button
                        onClick={() => openEdit(r)}
                        className="text-zinc-600 p-1 hover:bg-zinc-100 rounded mr-1"
                        title="Editează"
                        data-testid={`edit-vacation-${r.id}`}
                      >
                        <Pencil size={14}/>
                      </button>
                    )}
                    {canDeleteRow(r) && (
                      <button
                        onClick={() => remove(r)}
                        className="text-red-600 p-1 hover:bg-red-50 rounded"
                        title="Șterge"
                        data-testid={`delete-vacation-${r.id}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-zinc-500 italic">Niciun concediu.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Matrice lunară de disponibilitate — admin + planificator */}
      {(isAdmin || user?.role === "planificator") && (
        <AvailabilityMatrix persons={persons} vacations={rows} />
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editare concediu" : (isAdmin ? "Concediu nou" : "Cerere de concediu")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Persoană</Label>
              {isAdmin ? (
                <Select
                  value={form.person_id || "__free__"}
                  onValueChange={(v) => {
                    if (v === "__free__") {
                      setForm({ ...form, person_id: "", person_name: form.person_name || "" });
                    } else {
                      const p = persons.find((x) => x.id === v);
                      setForm({ ...form, person_id: v, person_name: p ? `${p.nume || ""} ${p.prenume || ""}`.trim() : "" });
                    }
                  }}
                >
                  <SelectTrigger data-testid="vacation-person-select"><SelectValue placeholder="Alege persoana"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__free__">— Text liber —</SelectItem>
                    {persons.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nume} {p.prenume}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.person_name} disabled className="bg-zinc-50" data-testid="vacation-person-locked" />
              )}
              {isAdmin && !form.person_id && (
                <Input
                  className="mt-2"
                  placeholder="Nume liber"
                  value={form.person_name}
                  onChange={(e) => setForm({ ...form, person_name: e.target.value })}
                  data-testid="vacation-person-free"
                />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>De la</Label><Input type="date" data-testid="vacation-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Până la</Label><Input type="date" data-testid="vacation-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div>
              <Label>Notă</Label>
              <Textarea data-testid="vacation-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="Motiv / observații" />
            </div>
            {!isAdmin && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Cererea va fi trimisă cu status <strong>În așteptare</strong> și va fi aprobată sau respinsă de un administrator.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button
              data-testid="submit-vacation"
              onClick={submit}
              className="bg-white border border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700 font-semibold"
            >
              {editing ? "Salvează" : (isAdmin ? "Adaugă" : "Trimite cererea")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectingId} onOpenChange={(v) => { if (!v) { setRejectingId(null); setRejectReason(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Respingere concediu</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            <Label>Motiv (opțional)</Label>
            <Textarea
              data-testid="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="ex: Suprapunere cu alt angajat"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectingId(null); setRejectReason(""); }}>Anulează</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitReject} data-testid="submit-reject">Respinge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={(v) => { if (!v) { setHistoryOpen(false); setHistoryRows(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="vacations-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History size={18} className="text-zinc-500"/> Istoric Persoane Concediu
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">
            Persoane care au revenit din concediu. Sunt disponibile pentru alocări în Management / Proiecte.
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {historyLoading ? (
              <div className="py-8 text-center text-sm text-zinc-400 italic">Se încarcă…</div>
            ) : !historyRows || historyRows.length === 0 ? (
              <div className="py-8 text-center text-sm text-zinc-400 italic">
                Nu există persoane în istoric. Confirmă „Revenirea" unui concediu pentru a-l muta aici.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr className="text-left">
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase">Persoană</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase">Început</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase">Sfârșit</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase text-center">Zile</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase">Revenit pe</th>
                    <th className="p-2 text-xs font-semibold text-zinc-500 uppercase">Notă</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--border)] row-hover" data-testid={`vacation-history-${r.id}`}>
                      <td className="p-2 font-medium">{r.person_name || "—"}</td>
                      <td className="p-2 tabular-nums">{fmtRoDate(r.start_date)}</td>
                      <td className="p-2 tabular-nums">{fmtRoDate(r.end_date)}</td>
                      <td className="p-2 text-center tabular-nums">
                        {(() => {
                          try {
                            const sd = new Date(r.start_date);
                            const ed = new Date(r.end_date);
                            if (isNaN(sd) || isNaN(ed)) return "—";
                            const days = Math.floor((ed - sd) / 86400000) + 1;
                            return days > 0 ? <span className="font-semibold">{days}</span> : "—";
                          } catch { return "—"; }
                        })()}
                      </td>
                      <td className="p-2 text-xs text-zinc-600">{String(r.returned_at || "").slice(0, 10) || "—"}</td>
                      <td className="p-2 text-zinc-600">{r.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setHistoryOpen(false); setHistoryRows(null); }}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
